import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { createListingDraft } from 'domains/listing/services/listingService';
import { setClientListingPrefill } from '../utils/storage';

const CATEGORY_LABELS = {
  cars: 'Cars',
  motorcycles: 'Motorcycles',
  furniture: 'Furniture',
  removals: 'Removals',
  pallets: 'Pallets',
  freight: 'Freight',
  parcels: 'Parcels',
  machinery: 'Machinery',
  other_vehicles: 'Other Vehicles',
  pets: 'Pets',
  building_materials: 'Building Materials',
  fragile: 'Fragile',
  boats: 'Boats',
  bicycles: 'Bicycles',
  vehicle_parts: 'Vehicle Parts',
  household: 'Household',
  other: 'Other',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

export default function AiListingDraftCard({ result, navigate }) {
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const draft = result.draft || {};

  const from = [draft.fromCity, draft.fromCountry].filter(Boolean).join(', ') || '—';
  const to = [draft.toCity, draft.toCountry].filter(Boolean).join(', ') || '—';
  const category = CATEGORY_LABELS[draft.category] || draft.category || null;
  const pickup = formatDate(draft.pickupDateFrom);
  const items = Array.isArray(draft.items) ? draft.items : [];

  const handlePublishDraft = async () => {
    setPublishing(true);
    setError('');
    try {
      const listing = await createListingDraft(draft);
      const listingId = listing.id || listing._id;
      navigate(`/my-listings/${listingId}`);
    } catch (err) {
      setError(err?.message || 'Failed to create draft. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleAddDetails = () => {
    setClientListingPrefill(draft);
    navigate('/create-listing');
  };

  return (
    <Box borderWidth="1px" borderRadius="xl" p={4} bg="blue.50" borderColor="blue.200">
      <Stack spacing={3}>
        <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.15em" color="blue.500">
          Prepared listing draft
        </Text>

        <Stack spacing={1}>
          <HStack justify="space-between" align="flex-start">
            <Text fontSize="sm" fontWeight="700" color="navy.700" flex={1} noOfLines={1}>
              {from} → {to}
            </Text>
            {category ? (
              <Badge colorScheme="purple" borderRadius="full" px={2} fontSize="10px" flexShrink={0}>
                {category}
              </Badge>
            ) : null}
          </HStack>

          {pickup ? (
            <Text fontSize="xs" color="gray.600">Pickup from: {pickup}</Text>
          ) : null}

          {items.length ? (
            <Text fontSize="xs" color="gray.600">
              {items.length} item{items.length !== 1 ? 's' : ''}
              {items[0]?.description ? ` — ${items[0].description}` : ''}
            </Text>
          ) : null}

          {draft.description ? (
            <Text fontSize="xs" color="gray.600" noOfLines={2}>{draft.description}</Text>
          ) : null}
        </Stack>

        {error ? (
          <Text fontSize="xs" color="red.500">{error}</Text>
        ) : null}

        <HStack spacing={2}>
          <Button size="sm" colorScheme="blue" onClick={handlePublishDraft} isLoading={publishing}>
            Publish draft
          </Button>
          <Button size="sm" variant="outline" colorScheme="blue" onClick={handleAddDetails} isDisabled={publishing}>
            Add more details
          </Button>
        </HStack>
      </Stack>
    </Box>
  );
}
